import { Component, ViewChild, Input, ElementRef, SimpleChanges, AfterViewInit, OnDestroy, OnChanges, OnInit } from "@angular/core";
import { Chart, ChartEvent, registerables } from "chart.js";
import { MixpanelService } from 'src/app/core/services/mixpanel.service';
import * as _ from "lodash";
import { LineChartConfig } from "../../types/chart-types";
import { AuthenticationService } from '../../services/authentication.service';



@Component({
    selector: "app-line-graph-common-chart",
    templateUrl: "./line-graph-common-chart.component.html",
    styleUrls: ["./line-graph-common-chart.component.scss"],
    standalone: false
})
export class LineGraphCommonChartComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges{
  @Input() config!: LineChartConfig;
  @Input() unique_id: string = "";

  @ViewChild("chartContainerEle") chartCanvas!: ElementRef;
  chart!: any;
  TOOLTIP_ID = "chart-js-tooltip";
  TOOLTIP_STYLE = `
    background: rgba(255, 255, 255);
    border-radius: 3px;
    color: black;
    opacity: 1;
    position: absolute;
    transform: translate(10%, 0);
    transition: all .1s ease;
    box-shadow: rgba(187, 187, 187, 0.5) 4px 4px 15px 0px;
  `;
  private currentTooltipData: any = null;
  dataPointHoverStartTime :any = null;

  constructor(private mixpanelService: MixpanelService, private _authenticationservice: AuthenticationService) {
    Chart.register(...registerables); // Register necessary components
  }

  ngOnInit() {
    // Set default values for optional configuration properties
    this.config = {
      displayYAxisGridLine: true,
      xAxisDisplay: false,
      borderWidth: 2,
      backgroundColor: "rgb(205, 235, 247)",
      borderColor: "rgb(76 172 229)",
      pointRadius: 0,
      pointHoverRadius: 0,
      yAxisLabel: '',
      mixPanelEventName: '',
      toolTipId: '',
      toolTipRequired: false,
      xAxisFontSize: 10,
      xAxisFontColor: '#838383',
      xAxisDisplayBorder: false,
      yAxisPadding: 5,
      yAxisDrawOnChartArea: false,
      showDottedSegment: false,
      fillArea: true,
      applyExtraPadding: false,
      useSimpleTooltip: false,
      ...this.config
    };
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes["config"]) {
      this.updateLineChart();
    }
  }

  ngAfterViewInit() {

    this.implementLineChart();
    if (this.config.mixPanelEventName!.trim()){
      this.setupHoverEvent(this.chartCanvas.nativeElement);
    }
  }

  ngOnDestroy() {
    this.chart?.destroy();
  }

  updateLineChart() {
    this.chart?.destroy();
    this.implementLineChart();
  }

  private getYValue(point: number | { y: number; noCallData: boolean }): number {
    return typeof point === 'object' ? point.y : point;
  }

  implementLineChart() {
    const canvas = document.getElementById('base-chart-canvas-' + this.unique_id);
    if (canvas != null && canvas instanceof HTMLCanvasElement) {
      const ctx = canvas.getContext('2d');
      if (ctx == null) {
        throw new Error('Cannot get 2d context from canvas');
      }

      const maxYValue = Math.max(...this.config.yAxis.map(this.getYValue));
      const lastIndex = this.config.xAxis.length - 1;
      const secondLastIndex = lastIndex - 1;

      this.chart = new Chart(ctx, {
        type: 'line',
        data: {
          labels: this.config.xAxis,
          datasets: [
            {
              data: this.config.yAxis.map(this.getYValue),
              backgroundColor: this.config.fillArea ? this.config.backgroundColor : undefined,
              borderColor: this.config.borderColor,
              fill: this.config.fillArea,
              borderWidth: this.config.borderWidth,
              segment: this.config.showDottedSegment ? {
                borderDash: (ctx) => {
                  const index = ctx.p0DataIndex;
                  return index >= secondLastIndex ? [3, 5] : undefined;
                },
              } : undefined,
              pointRadius: this.config.pointRadius,
              pointBackgroundColor: this.config.borderColor,
              pointBorderColor: this.config.pointBorderColor,
              pointHoverRadius: this.config.pointHoverRadius,
              pointHoverBorderColor: this.config.pointHoverBorderColor,
            },
          ],
        },
        options: {
          maintainAspectRatio: false,
          interaction: {
            mode: "index",
            intersect: false,
          },
          scales: {
            y: {
              ...(this.config.yAxisMaxValue !== undefined ? { max: this.config.yAxisMaxValue } : null),
              title: {
                display: !!this.config.yAxisLabel,
                text: this.config.yAxisLabel,
                font: {
                  family: 'Roboto',  // Set the font family to Roboto
                }
              },
              ticks: {
                ...this.config.yAxisInterval !== undefined ? { stepSize: this.config.yAxisInterval } : {},
                padding: this.config.yAxisPadding, // Increase this value to move numbers further from the axis
                font: {
                  size: this.config.yAxisFontSize || 12,
                  family: 'Roboto',
                },
              },
              stacked: true,
              border: { display: false },
              grid: {
                color: (context) => {
                  if (context.tick.value !== 0) {
                    return this.config.yAxisDrawOnChartArea ? "rgba(0, 0, 0, 0.1)" : 'rgba(0, 0, 0, 0)';
                  }
                  return 'rgba(0, 0, 0, 0)';
                },
                drawOnChartArea: this.config.yAxisDrawOnChartArea,
                drawTicks: false,
              },
              display: this.config.displayYAxisGridLine,
              beginAtZero: true,
              afterFit: (scaleInstance) => {
                if(this.config.applyExtraPadding) {
                  scaleInstance.max = maxYValue + 3; // Add a small padding for hover dots to show correctly
                }
              }
            },
            x: {
              border: { display: this.config.xAxisDisplayBorder },
              grid: {
                color: "rgba(0, 0, 0, 0.1)",
                drawOnChartArea: false,
                drawTicks: this.config.xAxisDisplayTicks,
                tickLength: 5
              },
              display: this.config.xAxisDisplay,
              ticks: {
                font: {
                  size: this.config.xAxisFontSize || 12,
                  family: 'Roboto',
                },
                color: this.config.xAxisFontColor || '#838383',
              }
            },
          },
          plugins: {
            legend: {
              display: false,
            },
            tooltip: this.getPlugInConfig(ctx)
          },
        },
      });
    }
  }


  public ChartHovered({
    event,
    active,
  }: {
    event?: ChartEvent;
    active?: any;
  }): void { }

  public chartClicked({
    event,
    active,
  }: {
    event?: ChartEvent; // Event associated with the chart click
    active?: any; // Array of active elements on the chart
  }): void { }

  externalTooltipHandler(context: any) {
    // Extract necessary information from the context
    const { chart, tooltip, dataIndex } = context;

    // Fetch or create the tooltip element
    let tooltipEl = chart.canvas.parentNode?.querySelector("#chart-js-tooltip");
    const chartContainer = context.chart.canvas.parentNode;

    if (!tooltipEl) {
      tooltipEl = document.createElement("div");
      tooltipEl.id = this.TOOLTIP_ID;
      tooltipEl.style.cssText = this.TOOLTIP_STYLE;

      const table = document.createElement("table");
      table.style.margin = "0";

      tooltipEl.appendChild(table);
      chart.canvas.parentNode.appendChild(tooltipEl);
    }

    // Hide tooltip if it's not visible
    if (tooltip.opacity === 0) {
      tooltipEl.style.opacity = '0';
      return;
    }

    // Extract data for the current data point and set the tooltip text accordingly
    const currentObject = context.tooltip.dataPoints;
    // Set Text
    if (tooltip.body) {
      // const bodyLines = tooltip.body.map((b: any) => b.lines);
      const tableRoot = tooltipEl.querySelector("table");

      // Remove old children
      while (tableRoot.firstChild) {
        tableRoot.removeChild(tableRoot.firstChild);
      }
      const tooltipEle = document.createElement("div");
      tooltipEle.setAttribute('id', this.config.toolTipId!);
      tooltipEle.innerHTML = `
        <div style="font-size: 12px; color: #8E9393;min-width:150px;">
          <div style="font-size: 30px; font-family: Roboto; font-weight: 400; color: #313233; padding-bottom: 5px;">
            ${currentObject[0].formattedValue} <span style="font-size:12px;color:#8E9393">locations</span>
          </div>
          <div style="font-size: 12px; color: #313233;">${this.formatDate(currentObject[0].label)}</div>
        </div>`;
      tableRoot.appendChild(tooltipEle);
    }

    // Calculate the position and display the tooltip
    let { left, top } = this.calculateTooltipPosition(chart, tooltipEl, chartContainer, tooltip);

    tooltipEl.style.opacity = "1";
    tooltipEl.style.left = left + "px";
    tooltipEl.style.top = top + "px";
    tooltipEl.style.font = tooltip.options.bodyFont.string;
    tooltipEl.style.padding = tooltip.options.padding + "px " + tooltip.options.padding + "px";
  }

  private calculateTooltipPosition(chart: any, tooltipEl: any, chartContainer: any, tooltip: any) {
    const { offsetLeft: positionX, offsetTop: positionY } = chart.canvas;

    const tooltipWidth = tooltipEl.offsetWidth;
    const tooltipHeight = tooltipEl.offsetHeight;
    const chartWidth = chartContainer.offsetWidth;
    const chartHeight = chartContainer.offsetHeight;

    let left = positionX + tooltip.caretX + 25;
    let top = positionY + (chartHeight - tooltipHeight) / 2;

    // Adjust the tooltip position to stay within the chart container
    if (left / 1.5 + tooltipWidth > chartWidth) {
      left = (chartWidth + tooltipWidth);
    }
    if (top + tooltipHeight > chartHeight) {
      top = (chartHeight - tooltipHeight) - 25;
    }
    return { left, top };
  }

  // Assuming this code is part of a component method
  setupHoverEvent(element: HTMLElement) {
    let hoverTimeout: number | null = null;
    let hoverStartTime: number | null = null;

    element.addEventListener('mouseenter', async () => {
      this.currentTooltipData = null;
      this.dataPointHoverStartTime = null;
      hoverStartTime = Date.now();
      //Set a timeout for 2 seconds
      if(this.config.mixPanelEventNamePH== undefined)
        hoverTimeout = window.setTimeout(() => {
          this.mixpanelService.track(this.config.mixPanelEventName!);
          }, 2000);
    });

    element.addEventListener('mouseleave', () => {
      // If the mouse leaves before 2 seconds, clear the timeout
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
        hoverTimeout = null;
      }
      if (this.currentTooltipData !== null && this.dataPointHoverStartTime !== null) {
        const timeSpent = (Date.now() - this.dataPointHoverStartTime) / 1000;
        if(timeSpent >= 1)
        {
          this.mixpanelService.track(this.config.mixPanelEventNamePH!, {
            ImpersonatedCustomer: this._authenticationservice.customerName ?? " ",
            ImpersonatedUser: this._authenticationservice.userName ?? " ",
            Month: this.currentTooltipData,
            DwellTime: timeSpent,
            accountNumber: this.config.accountNumber ?? 'NA'
        });
        }
      }
      hoverStartTime = null;
      this.currentTooltipData = null;
      this.dataPointHoverStartTime = null;
    });
  }

  //Method will return the date in the format of MM-YYYY
  private formatDate(date: string) {
    let [year, month] = date.split("-");
    let formatted_Date: string = `${month}-${year}`
    return formatted_Date;
  }

getPlugInConfig(ctx: any): any {
    if (!this.config.toolTipRequired) {
        return {
            enabled: false,
            position: "nearest",
        };
    }

    if (this.config.useSimpleTooltip) {
        return {
            enabled: true,
            mode: 'index',
            intersect: false,
            backgroundColor: 'white',
            titleColor: 'black',
            bodyColor: 'black',
            borderColor: '#e0e0e0',
            borderWidth: 1,
            padding: 10,
            callbacks: {
                label: (context: { parsed: { y: any; }; dataIndex: number; dataset: { label: any; }; }) => {
                    const dataPoint = this.config.yAxis[context.dataIndex];
                    const dataPointX = this.config.xAxis[context.dataIndex];
                    if (typeof dataPoint === 'object' && dataPoint.noCallData) {
                        return 'No Call Data';
                    }
                    const value = context.parsed.y;
                    const formattedValue = value !== null ? value : 'N/A';
                    if (this.currentTooltipData !== dataPointX) {
                        if (this.currentTooltipData !== null && this.dataPointHoverStartTime !== null) {
                           const timeSpent = (Date.now() - this.dataPointHoverStartTime) / 1000; 
                            if(timeSpent >= 1)
                            {
                              this.mixpanelService.track(this.config.mixPanelEventNamePH!, {
                                ImpersonatedCustomer: this._authenticationservice.customerName ?? " ",
                                ImpersonatedUser : this._authenticationservice.userName ?? " ",
                                Month: this.currentTooltipData,
                                DwellTime: timeSpent,
                                accountNumber: this.config.accountNumber ?? 'NA'
                            });
                            }
                        }
                        this.currentTooltipData = dataPointX;
                        this.dataPointHoverStartTime = Date.now(); 
                    }

                    return `${context.dataset.label || ''}  ${formattedValue}`;
                },
            }
        };
    } else {
        return {
            enabled: false,
            position: "nearest",
            external: (context: any) => this.externalTooltipHandler(context), 
        }
      }
    }
}
